import type { NoteDto } from "@repo/data-store";
import { MarkdownContent } from "@repo/ui/components/markdown-content";
import { Card, CardContent, CardHeader, CardTitle } from "@repo/ui/components/ui/card";
import { cn } from "@repo/ui/lib/utils";
import { Link } from "@tanstack/react-router";
import { Calendar } from "lucide-react";
import type { ReactNode } from "react";
import type { NoteEntry } from "@/features/notes";

/** Sort by date desc; missing date sinks to the bottom. */
export function sortByDateDesc(items: NoteDto[]): NoteDto[] {
  return [...items].sort((a, b) => {
    const ta = pickDate(a) ? Date.parse(pickDate(a) as string) : Number.NEGATIVE_INFINITY;
    const tb = pickDate(b) ? Date.parse(pickDate(b) as string) : Number.NEGATIVE_INFINITY;
    return tb - ta;
  });
}

function pickDate(ci: NoteDto): string | undefined {
  return ci.date ?? ci.effectiveDateTime ?? ci.effectivePeriod?.start;
}

function Root({ children }: { children: ReactNode }) {
  return <Card size="sm">{children}</Card>;
}

function Header({ title = "Notes", action }: { title?: string; action?: ReactNode }) {
  return (
    <CardHeader>
      <div className="flex items-center justify-between w-full">
        <CardTitle className="text-sm font-semibold">{title}</CardTitle>
        {action}
      </div>
    </CardHeader>
  );
}

function Body({ children }: { children: ReactNode }) {
  return (
    <CardContent>
      <div className="space-y-4">{children}</div>
    </CardContent>
  );
}

function Empty({ children = "No notes yet." }: { children?: ReactNode }) {
  return <p className="text-xs text-muted-foreground">{children}</p>;
}

interface ItemProps {
  entry: NoteEntry;
  /** When set, wraps the row in a Link to the case (note attributed to that case's encounter). */
  linkToCase?: { patientId: string; caseId: string };
}

/** Labelled as the Notes tab labels it: title, then type and date, then a plain-text preview. */
function Item({ entry, linkToCase }: ItemProps) {
  const meta = [entry.categoryLabel, entry.dateLabel].filter(Boolean).join(" · ");

  const wrapperClass = cn(
    "block min-w-0 border-l-2 border-border pl-2.5 py-0.5",
    linkToCase && "hover:bg-muted/50 rounded-r",
  );

  const inner = (
    <>
      <div className="text-sm font-medium leading-snug line-clamp-1">{entry.title}</div>
      <div className="flex items-center gap-1.5 mt-0.5 text-xs text-muted-foreground">
        <span>{meta}</span>
        {linkToCase && (
          <span className="inline-flex items-center gap-1">
            <span className="text-muted-foreground/50">·</span>
            <Calendar className="h-3 w-3" />
            On case
          </span>
        )}
      </div>
      {entry.text.trim() && (
        // A preview, not the note: cut off at a few lines and faded, with
        // headings brought down to the card's text size.
        <MarkdownContent className="mt-1 max-h-20 overflow-hidden text-xs text-muted-foreground [mask-image:linear-gradient(to_bottom,black_60%,transparent)] [&_*]:my-0.5 [&_*]:py-0 [&_h1]:text-xs [&_h2]:text-xs [&_h3]:text-xs [&_h4]:text-xs [&_h5]:text-xs [&_h6]:text-xs [&_h1,&_h2,&_h3,&_h4,&_h5,&_h6]:mt-1.5 [&_h1,&_h2,&_h3,&_h4,&_h5,&_h6]:text-foreground">
          {entry.text}
        </MarkdownContent>
      )}
    </>
  );

  if (linkToCase) {
    return (
      <Link to="/patients/$patientId/cases/$caseId" params={linkToCase} className={wrapperClass} title="Open case">
        {inner}
      </Link>
    );
  }
  return <div className={wrapperClass}>{inner}</div>;
}

export const NoteList = Object.assign(Root, {
  Header,
  Body,
  Empty,
  Item,
});
